import { get, set } from "lodash";
import {
  RecordSelectAsyncDatasource,
  RecordSelectAsyncTemplateOptions,
} from "./types";
import { IRecordRepeaterTemplateOptions } from "./types/repeater/controller";
import { isArray, IScope } from "angular";
import { LookupManagementService } from "../../lookup/lookupManagement";
import {
  IExpressionFunction,
  IFieldRuntimeObject,
  ITemplateScope,
  IValidator,
  IWatcher,
} from "AngularFormly";
import {
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../views/recordGridView";
import {
  LookupModalService,
  vsServiceName as LookupModalServiceName,
} from "../../lookup/lookupModal";
import {
  SelectUserFieldConfig,
  vsTypeName as userAuthType,
} from "./types/userAuth";
import { IRecordEditRepeaterTemplateOptions } from "./types/repeater/editController";

export const vsName = "fieldConfigurationHelperService";

export interface LookupFieldConfig {
  hideAddButton?: boolean;
  watcher?: IWatcher | IWatcher[] | undefined;
  referenceFieldKey?: any;
  expressionProperties?:
    | { [key: string]: string | IExpressionFunction | IValidator }
    | undefined;
  label?: string;
  key: string;
  defaultTake?: number;
  getDefaultItem?: (model: any) => { Id: any; Name: any };
  objectName: string;
  required?: boolean;
  hideExpression?: string | IExpressionFunction;
  className?: string;
  defaultSort?: string;
  readonly?: boolean;
  customDatasource?: RecordSelectAsyncDatasource; // This can be a specific type if known
}

export class FieldConfigurationHelperService {
  static $inject = [RecordGridViewServiceName, LookupModalServiceName];

  constructor(
    private readonly recordGridViewService: RecordGridViewService,
    private readonly lookupModalService: LookupModalService
  ) {}

  lookupField(
    lookupManagementService: LookupManagementService,
    config: LookupFieldConfig
  ) {
    const gridConfiguration = this.recordGridViewService.getGridConfiguration(
      config.objectName
    )!;

    const referenceFieldKey = config.referenceFieldKey
      ? config.referenceFieldKey
      : config.key!.substring(0, config.key!.length - 2);

    const defaultItemFactory =
      config.getDefaultItem ?? FieldConfigurationHelperService.getDefaultItem;

    const fieldDefinition = this.selectAsyncField({
      queryUrl: gridConfiguration.gridDataSource!.url,
      getDefaultItem: (formModel: any) =>
        defaultItemFactory(get(formModel ?? {}, referenceFieldKey)),
      label: config.label ?? gridConfiguration.nameSingular,
      key: config.key,
      required: config.required,
      expressionProperties: config.expressionProperties ?? {},
      hideExpression: config.hideExpression,
      watcher: config.watcher,
      onSelect: (selectedItem: any, model: any) =>
        onSelect(selectedItem, model),
      className: config.className,
      customDatasource: config.customDatasource,
      defaultTake: config.defaultTake,
      queryBuilder: (search: string) => {
        return {
          NameContains: search,
          Disabled: false,
          OrderBy: config.defaultSort ?? gridConfiguration.defaultSort,
        };
      },
      readonly: config.readonly,
    });

    if (!(config.hideAddButton ?? false)) {
      fieldDefinition.templateOptions!.addonRight =
        this.lookupModalService.getDropdownAddAddon(
          lookupManagementService,
          config.objectName,
          (selectedItem: any, _: IScope, model: any) => {
            onSelect(selectedItem, model);
            set(model, config.key, selectedItem.Id);
          },
          lookupManagementService.config.defaultModifyPermission ?? ""
        );
    }
    return fieldDefinition;

    function onSelect(selectedItem: any, formModel: any) {
      set(formModel, referenceFieldKey, selectedItem);
    }
  }

  selectUserField(
    config: SelectUserFieldConfig
  ): AngularFormly.IFieldConfigurationObject {
    return {
      key: config.key,
      name: config.name ?? config.key,
      type: userAuthType,
      expressionProperties: config.expressionProperties,
      templateOptions: {
        label: config.label,
        initialItemSelector: config.initialItemSelector,
        onSelect: config.onSelect,
      },
      hideExpression: config.hideExpression,
      watcher: config.watcher,
      className: config.className,
    };
  }

  selectAsyncField(
    config: RecordSelectAsyncTemplateOptions
  ): AngularFormly.IFieldConfigurationObject {
    return {
      key: config.key,
      name: config.name ?? config.key,
      type: "recordSelectAsync",
      templateOptions: config,
      model: config.model,
      expressionProperties: config.expressionProperties,
      hideExpression: config.hideExpression,
      watcher: config.watcher,
      className: config.className,
    };
  }

  gridSelectAsyncField(
    config: RecordSelectAsyncTemplateOptions
  ): AngularFormly.IFieldConfigurationObject {
    return {
      key: config.key,
      name: config.name ?? config.key,
      type: "recordGridSelectAsync",
      templateOptions: config,
      model: config.model,
      expressionProperties: config.expressionProperties,
      watcher: config.watcher,
      className: config.className,
    };
  }

  editRepeaterField(config: IRecordEditRepeaterTemplateOptions) {
    return {
      key: config.key,
      name: config.name ?? config.key,
      type: "recordEditRepeater",
      id: config.name ?? config.key,
      templateOptions: config,
    };
  }

  repeaterField(
    config: IRecordRepeaterTemplateOptions
  ): AngularFormly.IFieldConfigurationObject {
    return {
      key: config.key,
      name: config.name ?? config.key,
      type: "recordRepeater",
      id: config.name ?? config.key,
      templateOptions: config,
      hideExpression: config.hideExpression,
      watcher: config.watcher,
    };
  }

  static getDefaultItem(model: any) {
    return {
      ...model,
    };
  }
}

export class FieldConfigurationHelperWatchers {
  static nullWhenNotValue(
    watch: string,
    value: string,
    fieldsToDelete?: string[] | string
  ): IWatcher {
    fieldsToDelete ??= [];
    const fields = isArray(fieldsToDelete) ? fieldsToDelete : [fieldsToDelete];
    return {
      expression: watch,
      listener: (
        field: IFieldRuntimeObject,
        newValue: any,
        _: any,
        scope: ITemplateScope
      ) => {
        const { form, model } = scope;

        if (newValue !== value && form.$dirty) {
          if (field.key) {
            set(model ?? {}, field.key, undefined);
          }

          fields.forEach((f) => {
            set(model ?? {}, f, undefined);
          });
        }
      },
    };
  }
}
